import { useState, useEffect } from "react";
import { getProducts, deleteProduct } from "./services/source";
import {
  Box,
  CssBaseline,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Routes, Route } from "react-router-dom";
import AppNavbar from "./components/navbar";
import BottomNav from "./components/bottomNavigator";
import Dashboard from "./screen/dashboard";
import ShoppingCart from "./components/shoppingCard";
import Armazem from "./components/armazem";
import Compras from "./components/compras";
import Produtos from "./components/produtos";
import Vendas from "./components/vendas";

import colors from "./config/colors";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import InventoryIcon from "@mui/icons-material/Inventory";
import HomeIcon from "@mui/icons-material/Home";
import RestoreIcon from "@mui/icons-material/Restore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate, useLocation } from "react-router-dom";
import Upload from "./components/upload";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "./context/AuthContext";

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [locationFilter, setLocationFilter] = useState("All");

  const loadProducts = async () => {
    try {
      const data = await getProducts();

      const productsWithImage = data.map((item) => ({
        ...item,
        image: `${process.env.REACT_APP_API_URL}/${item.image}`,
      }));

      setProducts(productsWithImage);
      setDisplayProducts(productsWithImage);
    } catch (error) {
      console.log(error);
    }
  };

  const { customer } = useAuth();
  const isAdmin = customer?.role === "admin";

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts =
    locationFilter === "All"
      ? displayProducts
      : displayProducts.filter(
          (product) =>
            product.location?.toLowerCase() === locationFilter.toLowerCase(),
        );

  const handleAddCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.title === product.title);

      if (existing) {
        return prev.map((item) =>
          item.title === product.title
            ? {
                ...item,
                quantity: item.quantity + product.quantity,
              }
            : item,
        );
      }

      return [...prev, product];
    });
  };

  const handleDeleteProduct = async (id) => {
    try {
      const response = await deleteProduct(id);

      if (response.data.success) {
        await loadProducts();
        alert("Product deleted");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const showRecentProducts = () => {
    const sorted = [...products].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );

    setDisplayProducts(sorted);

    navigate("/");
  };

  const showFavoriteProducts = () => {
    const sorted = [...products].sort(
      (a, b) => Number(b.rating) - Number(a.rating),
    );

    setDisplayProducts(sorted);

    navigate("/");
  };

  const actions = [
    {
      label: "Home",
      icon: <HomeIcon />,
      color: colors.primary,
      onClick: async () => {
        await loadProducts();
        navigate("/");
      },
    },
    ...(isAdmin && location.pathname === "/"
      ? [
          {
            label: "Upload",
            icon: <CloudUploadIcon />,
            color: colors.primary,
            onClick: () => setUploadOpen(true),
          },
        ]
      : []),
    {
      label: "Compras",
      icon: <ShoppingBasketIcon />,
      color: colors.primary,
      onClick: () => navigate("/compras"),
    },

    ...(isAdmin && location.pathname === "/"
      ? [
          {
            label: "Saída Armazém",
            icon: <InventoryIcon />,
            color: colors.primary,
            onClick: () => navigate("/armazem"),
          },
          {
            label: "Vendas",
            icon: <PriceChangeIcon />,
            color: colors.primary,
            onClick: () => navigate("/vendas"),
          },
          {
            label: "Produtos",
            icon: <QrCodeScannerIcon />,
            color: colors.primary,
            onClick: () => navigate("/produtos"),
          },
        ]
      : []),

    {
      label: "Recents",
      icon: <RestoreIcon />,
      color: colors.primary,
      tooltip: "Adicionados recentemente",
      onClick: showRecentProducts,
    },

    {
      label: "Favorites",
      icon: <FavoriteIcon />,
      color: colors.primary,
      onClick: showFavoriteProducts,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        m: 2,
      }}
    >
      <CssBaseline />

      {/* <AppNavbar cart={cart} openCart={() => setCartOpen(true)} /> */}
      <AppNavbar
        cart={cart}
        openCart={() => setCartOpen(true)}
        locationFilter={locationFilter}
        setLocationFilter={setLocationFilter}
        showLocationFilter={location.pathname === "/"}
      />

      {/* SHOPPING CART DRAWER */}
      <ShoppingCart
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        setCart={setCart}
      />

      {/* PAGES */}
      <Box sx={{ mt: 10 }}>
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                cart={cart}
                products={filteredProducts}
                onAddCart={handleAddCart}
                onDeleteProduct={handleDeleteProduct}
              />
            }
          />

          <Route path="/armazem" element={<Armazem />} />
          <Route path="/compras" element={<Compras />} />
          <Route path="/vendas" element={<Vendas />} />
          <Route
            path="/produtos"
            element={<Produtos refreshProducts={loadProducts} />}
          />
        </Routes>
      </Box>

      {/* <BottomNav actions={actions} /> */}
      <Paper
        elevation={5}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 1200,

          overflowX: "auto",

          "&::-webkit-scrollbar": {
            display: "none",
          },

          scrollbarWidth: "none",

          display: "flex",
        }}
      >
        <BottomNav actions={actions} />
      </Paper>

      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Upload Product
          <IconButton
            onClick={() => setUploadOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Upload refreshProducts={loadProducts} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default App;
